import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse, InternalAxiosRequestConfig } from 'axios';
import { toast } from 'react-toastify';
import { env } from '../utils/env';

// Configuração base da API
const API_BASE_URL = env.API_URL;

console.log('🔧 API Base URL configurada:', API_BASE_URL);

// Instância do Axios
export const api: AxiosInstance = axios.create({
  baseURL: API_BASE_URL + '/api',
  timeout: env.API_TIMEOUT,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Interceptor para adicionar o token de autenticação
api.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const token = localStorage.getItem('sysup_token');
    if (token && config.headers) {
      config.headers['Authorization'] = `Bearer ${token}`;
    }
    // FormData: remover Content-Type para o navegador definir multipart/form-data com boundary
    if (config.data instanceof FormData && config.headers) {
      delete config.headers['Content-Type'];
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Interceptor para tratar respostas e erros
api.interceptors.response.use(
  (response: AxiosResponse) => {
    // Retorna apenas os dados da resposta
    return response.data;
  },
  (error) => {
    // Tratamento de erros específicos
    if (error.response) {
      const { status, data } = error.response;

      switch (status) {
        case 401:
          // Token inválido ou expirado
          if (!(error.config as SysupAxiosRequestConfig | undefined)?.skipAuthRedirect) {
            localStorage.removeItem('sysup_token');
            if (window.location.pathname !== '/login') {
              window.location.href = '/login?sessao=expirada';
            }
          }
          break;

        case 403:
          // Acesso negado ou licença bloqueada
          if (data && data.error === 'LICENCA_BLOQUEADA') {
            toast.error(`⚠️ LICENÇA: ${data.message || 'Licença suspensa ou bloqueada'}`, {
              toastId: 'licenca-bloqueada',
              autoClose: 10000,
            });
            // Disparar evento para componentes ouvirem bloqueio se necessário
            window.dispatchEvent(new CustomEvent('sysup_license_blocked', { detail: data }));
          } else {
            toast.error('Acesso negado. Você não tem permissão para esta ação.', {
              toastId: 'acesso-negado',
            });
          }
          break;

        case 404:
          // Recurso não encontrado
          toast.error('Recurso não encontrado.', {
            toastId: 'recurso-nao-encontrado',
          });
          break;

        case 400:
          // Erros de dados duplicados ou validação específica
          // Não mostrar toast automático para permitir tratamento customizado
          // Mas ainda assim modificar o erro para incluir os dados estruturados
          error.response.data = data;
          break;

        case 422:
          // Erros de validação
          if (data.errors && Array.isArray(data.errors)) {
            data.errors.forEach((errorMsg: string) => {
              toast.error(errorMsg, {
                toastId: `validacao-${errorMsg}`,
              });
            });
          } else {
            toast.error(data.message || 'Dados inválidos fornecidos.', {
              toastId: 'dados-invalidos',
            });
          }
          break;

        case 429:
          toast.error('Muitas tentativas. Aguarde alguns minutos antes de tentar novamente.', {
            toastId: 'rate-limit',
          });
          break;

        case 500:
          toast.error('Erro interno do servidor. Tente novamente mais tarde.', {
            toastId: 'erro-servidor-500',
          });
          break;

        default:
          toast.error(data.message || 'Erro inesperado. Tente novamente.', {
            toastId: `erro-${status || 'desconhecido'}`,
          });
      }
    } else if (error.request) {
      // Erro de rede
      if (error.code === 'ECONNABORTED') {
        toast.error('Timeout: A requisição demorou muito tempo. Tente novamente.', {
          toastId: 'erro-timeout',
        });
      } else {
        toast.error('Erro de conexão. Verifique sua internet e tente novamente.', {
          toastId: 'erro-conexao',
        });
      }
    } else {
      // Outro tipo de erro
      toast.error('Erro inesperado. Tente novamente.', {
        toastId: 'erro-inesperado',
      });
    }

    return Promise.reject(error);
  }
);

// Tipos para facilitar o uso
export interface ApiResponse<T = any> {
  success: boolean;
  data?: T;
  message?: string;
  errors?: string[];
  pagination?: {
    page: number;
    limit: number;
    total: number;
    pages: number;
  };
}

interface SysupAxiosRequestConfig extends AxiosRequestConfig {
  skipAuthRedirect?: boolean;
}

// Funções utilitárias para requisições
export const apiRequest = {
  get: <T = any>(url: string, config?: AxiosRequestConfig): Promise<ApiResponse<T>> => {
    return api.get(url, config);
  },

  post: <T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<ApiResponse<T>> => {
    return api.post(url, data, config);
  },

  put: <T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<ApiResponse<T>> => {
    return api.put(url, data, config);
  },

  patch: <T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<ApiResponse<T>> => {
    return api.patch(url, data, config);
  },

  delete: <T = any>(url: string, config?: AxiosRequestConfig): Promise<ApiResponse<T>> => {
    return api.delete(url, config);
  },
};

// Função para upload de arquivos
export const uploadFile = async (
  url: string,
  file: File,
  onProgress?: (progress: number) => void
): Promise<ApiResponse> => {
  const formData = new FormData();
  formData.append('file', file);

  return api.post(url, formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
    onUploadProgress: (progressEvent) => {
      if (onProgress && progressEvent.total) {
        const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
        onProgress(progress);
      }
    },
  });
};

// Função para download de arquivos
export const downloadFile = async (url: string, filename?: string): Promise<void> => {
  try {
    const response = await api.get(url, {
      responseType: 'blob',
    });

    // Cria um link temporário para download
    const blob = new Blob([response.data]);
    const downloadUrl = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = downloadUrl;
    link.download = filename || 'download';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(downloadUrl);
  } catch (error) {
    toast.error('Erro ao baixar arquivo');
    throw error;
  }
};

export default api; 
